import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { hasAction, isStrictAdmin } from "@/lib/rbac";
import { Stamp, Save, Loader2, AlertCircle, Check, Upload } from "lucide-react";

const AREA = "hr_documents";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const EMPTY = {
  area: AREA,
  approver_user_id: "",
  approver_name: "",
  signatory_name: "",
  signatory_designation: "",
  signature_data_url: "",
  stamp_data_url: "",
  default_requires_signature: true,
  default_requires_stamp: false,
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function DocumentApprovalsSettings() {
  const { user } = useAuth();
  const canEdit = isStrictAdmin(user) || hasAction(user, "document_approvals", "edit");
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, u] = await Promise.all([
        api.get(`/document-approval-settings/${AREA}`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      const data = s.data || {};
      setForm({
        ...EMPTY,
        ...data,
        area: AREA,
        approver_user_id: data.approver_user_id || "",
      });
      setUsers((u.data || []).filter((x) => x.is_active !== false));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        label: `${u.name || u.email || u.id}${u.role ? ` (${u.role})` : ""}`,
      })),
    [users],
  );

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onApproverChange = (id) => {
    const u = users.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      approver_user_id: id || "",
      approver_name: u?.name || "",
    }));
  };

  const onImagePick = async (key, file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setErr("Please choose an image file (PNG/JPEG)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr("Image must be under 2 MB");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setField(key, dataUrl);
      setErr("");
    } catch {
      setErr("Could not read image");
    }
  };

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    setMsg("");
    setErr("");
    try {
      const payload = {
        approver_user_id: form.approver_user_id || null,
        approver_name: form.approver_name || "",
        signatory_name: form.signatory_name || "",
        signatory_designation: form.signatory_designation || "",
        signature_data_url: form.signature_data_url || "",
        stamp_data_url: form.stamp_data_url || "",
        default_requires_signature: !!form.default_requires_signature,
        default_requires_stamp: !!form.default_requires_stamp,
      };
      const r = await api.put(`/document-approval-settings/${AREA}`, payload);
      setForm({ ...EMPTY, ...r.data, area: AREA, approver_user_id: r.data?.approver_user_id || "" });
      setMsg("Settings saved");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Document Approvals" breadcrumbs={["Home", "Settings", "Document Approvals"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm" data-testid="document-approvals-settings">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Stamp className="h-5 w-5 text-[#226DB4]" /> HR Documents — approver &amp; signatory
          </h3>
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              data-testid="doc-approvals-save"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          )}
        </div>

        <div className="p-5 space-y-5">
          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
            </div>
          )}
          {msg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded text-sm flex items-center gap-2">
              <Check className="h-4 w-4" /> {msg}
            </div>
          )}

          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500">
                Configure who approves HR letters and which signatory/signature is stamped when a letter is Issued.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Approver user</label>
                  <select
                    value={form.approver_user_id || ""}
                    onChange={(e) => onApproverChange(e.target.value)}
                    disabled={!canEdit}
                    className={inputCls}
                    data-testid="doc-approvals-approver"
                  >
                    <option value="">— Anyone with approve permission / Admin —</option>
                    {userOptions.map((u) => (
                      <option key={u.id} value={u.id}>{u.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Approver name (display)</label>
                  <input
                    value={form.approver_name || ""}
                    onChange={(e) => setField("approver_name", e.target.value)}
                    disabled={!canEdit}
                    className={inputCls}
                    data-testid="doc-approvals-approver-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Signatory name</label>
                  <input
                    value={form.signatory_name || ""}
                    onChange={(e) => setField("signatory_name", e.target.value)}
                    disabled={!canEdit}
                    className={inputCls}
                    placeholder="e.g. Priya Sharma"
                    data-testid="doc-approvals-signatory-name"
                  />
                  <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                    If Signatory name and designation are empty, letters use the template factory closing (e.g. Human Resources). Clear the signature image too if you want the factory fallback.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Signatory designation</label>
                  <input
                    value={form.signatory_designation || ""}
                    onChange={(e) => setField("signatory_designation", e.target.value)}
                    disabled={!canEdit}
                    className={inputCls}
                    placeholder="e.g. HR Manager"
                    data-testid="doc-approvals-signatory-designation"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ImageUpload
                  label="Signature"
                  hint="Upload any signature scan (PNG/JPEG). Background is removed and ink is converted to professional black for letters."
                  value={form.signature_data_url}
                  disabled={!canEdit}
                  transparentPreview
                  onClear={() => setField("signature_data_url", "")}
                  onFile={(f) => onImagePick("signature_data_url", f)}
                  testid="doc-approvals-signature"
                />
                <ImageUpload
                  label="Stamp"
                  hint="Optional company stamp. White background is removed on save; original stamp colors are kept."
                  value={form.stamp_data_url}
                  disabled={!canEdit}
                  transparentPreview
                  onClear={() => setField("stamp_data_url", "")}
                  onFile={(f) => onImagePick("stamp_data_url", f)}
                  testid="doc-approvals-stamp"
                />
              </div>

              <div className="flex flex-wrap gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!form.default_requires_signature}
                    onChange={(e) => setField("default_requires_signature", e.target.checked)}
                    disabled={!canEdit}
                    className="h-4 w-4 rounded border-slate-300 text-[#226DB4] focus:ring-[#226DB4]"
                    data-testid="doc-approvals-req-signature"
                  />
                  Default requires signature
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!form.default_requires_stamp}
                    onChange={(e) => setField("default_requires_stamp", e.target.checked)}
                    disabled={!canEdit}
                    className="h-4 w-4 rounded border-slate-300 text-[#226DB4] focus:ring-[#226DB4]"
                    data-testid="doc-approvals-req-stamp"
                  />
                  Default requires stamp
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function ImageUpload({ label, value, onFile, onClear, disabled, testid, hint, transparentPreview }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
      <div className="text-xs font-semibold text-slate-600 uppercase mb-1">{label}</div>
      {hint ? <p className="text-[11px] text-slate-500 mb-2 leading-snug">{hint}</p> : null}
      {value ? (
        <div
          className={`mb-3 border border-slate-200 rounded p-3 flex items-center justify-center min-h-[88px] ${
            transparentPreview ? "" : "bg-white"
          }`}
          style={
            transparentPreview
              ? {
                  backgroundImage:
                    "linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)",
                  backgroundSize: "16px 16px",
                  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
                  backgroundColor: "#fff",
                }
              : undefined
          }
        >
          <img src={value} alt={label} className="max-h-20 max-w-full object-contain" />
        </div>
      ) : (
        <div className="mb-3 bg-white border border-dashed border-slate-300 rounded p-6 text-center text-xs text-slate-400">
          No image uploaded
        </div>
      )}
      <div className="flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border cursor-pointer ${
            disabled
              ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400"
              : "border-[#226DB4] text-[#226DB4] hover:bg-[#E8F0FF]"
          }`}
        >
          <Upload className="h-3.5 w-3.5" /> Upload
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            disabled={disabled}
            data-testid={testid}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = "";
            }}
          />
        </label>
        {value && !disabled && (
          <button
            type="button"
            onClick={onClear}
            className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
